import { useState } from "react";
import { SectionCard } from "shared/app/SectionCard";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Select,
  Show
} from "shared/components";
import { useFormTyped, type Register } from "shared/hooks";
import { Row } from "views/applications-id-report/components/Row";
import { AmenitiesForm, type PetPolicyField } from "shared/app/AmenitiesForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { Unit } from "types/landlord/address";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { AmenitiesSelect } from "shared/app/AmenitiesForm/components/AmenitiesSelect";
import { PetPolicyPmsField } from "shared/app/AmenitiesForm/components/PetPolicyPmsField";
import {
  type DogSizeValue,
  dogSizeOptions,
  getDogsAllowedLabel,
  toDogSizePayload,
  toDogSizeValue
} from "shared/app/AmenitiesForm/dog-size";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";

const getYesNo = (value?: boolean | null) => {
  if (value == null) return "-";
  return value ? "Yes" : "No";
};

const emptyPetPolicyTouched: Record<PetPolicyField, boolean> = {
  cats_allowed: false,
  dogs_allowed: false,
  dog_size: false
};

const buildPetPolicyPayload = (
  touched: Record<PetPolicyField, boolean>,
  values: {
    cats_allowed?: boolean;
    dogs_allowed?: boolean;
    dog_size: DogSizeValue;
  }
) => ({
  ...(touched.cats_allowed && { cats_allowed: values.cats_allowed }),
  ...(touched.dogs_allowed && { dogs_allowed: values.dogs_allowed }),
  ...((touched.dogs_allowed || touched.dog_size) && {
    dog_size: values.dogs_allowed ? toDogSizePayload(values.dog_size) : null
  })
});

const AmenitiesList = ({ amenities }: { amenities: Unit["amenities"] }) => {
  if (!amenities?.length)
    return <p className="font-medium text-content-1000">-</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {amenities.map((amenity) => (
        <Badge key={amenity.id} variant={BadgeVariant.BASE} size={BadgeSize.SM}>
          {amenity.title}
        </Badge>
      ))}
    </div>
  );
};

interface PmsEditAmenitiesFormValues {
  dogs_allowed?: boolean;
  cats_allowed?: boolean;
  dog_size: DogSizeValue;
  amenities: number[];
  pms_overrides: {
    cats_allowed?: boolean;
    dogs_allowed?: boolean;
    amenities?: boolean;
  };
}

const PmsEditAmenitiesForm = ({
  onClose,
  initialValues,
  unit
}: {
  onClose: () => void;
  initialValues: PmsEditAmenitiesFormValues;
  unit: Unit;
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);
  const [petPolicyTouched, setPetPolicyTouched] = useState<
    Record<PetPolicyField, boolean>
  >(emptyPetPolicyTouched);
  const overrides = values.pms_overrides;
  const dogsSynced = overrides?.dogs_allowed ?? true;

  const { mutate: updateAmenities, isPending } = useMutation({
    mutationFn: () => {
      const petPolicy = buildPetPolicyPayload(petPolicyTouched, values);
      return instance.patch(`/portal/v1/units/${unit.id}`, {
        amenity_ids: values.amenities,
        ...(Object.keys(petPolicy).length > 0 && { pet_policy: petPolicy }),
        pms_overrides: {
          ...unit.pms_overrides,
          ...values.pms_overrides,
          dog_size: dogsSynced
        }
      });
    },
    onSuccess: () => {
      toast.success("Amenities updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["unit"]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update amenities: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => updateAmenities()
  });

  const handlePetPolicyChange = (field: PetPolicyField, value: unknown) => {
    setPetPolicyTouched((prev) => ({ ...prev, [field]: true }));
    register(field).onChange(value);
  };

  const handleOverrideChange = (
    field: "cats_allowed" | "dogs_allowed",
    synced: boolean
  ) => {
    const newOverrides = { ...overrides, [field]: synced };
    register("pms_overrides").onChange(newOverrides);

    if (!synced) return;

    const pmsValues = unit.pms_values ?? {};
    handlePetPolicyChange(field, pmsValues[field] ?? null);
    if (field === "dogs_allowed")
      handlePetPolicyChange("dog_size", toDogSizeValue(pmsValues.dog_size));
  };

  return (
    <SectionCard
      title="Amenities"
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    >
      <div className="space-y-8">
        <PetPolicyPmsField
          id="cat-allowed"
          label="Cat allowed"
          radioName="cats_allowed"
          selectProps={register("cats_allowed")}
          synced={overrides?.cats_allowed ?? true}
          pmsValue={unit.pms_values?.cats_allowed}
          onValueChange={(val) => handlePetPolicyChange("cats_allowed", val)}
          onSyncChange={(val) => handleOverrideChange("cats_allowed", val)}
        />

        <PetPolicyPmsField
          id="dog-allowed"
          label="Dog allowed"
          radioName="dogs_allowed"
          selectProps={register("dogs_allowed")}
          synced={dogsSynced}
          pmsValue={unit.pms_values?.dogs_allowed}
          pmsValueLabel={
            unit.pms_values?.dogs_allowed == null
              ? undefined
              : getDogsAllowedLabel(
                  unit.pms_values.dogs_allowed,
                  unit.pms_values.dog_size
                )
          }
          onValueChange={(val) => handlePetPolicyChange("dogs_allowed", val)}
          onSyncChange={(val) => handleOverrideChange("dogs_allowed", val)}
        >
          <Show if={values.dogs_allowed}>
            <div className="mt-4">
              <Select
                id="dog-size"
                label="Dog size"
                options={dogSizeOptions}
                value={values.dog_size}
                isDisabled={dogsSynced}
                onChange={(val) => handlePetPolicyChange("dog_size", val)}
              />
            </div>
          </Show>
        </PetPolicyPmsField>

        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <AmenitiesSelect
              level="unit"
              onSelect={(value) => register("amenities").onChange(value)}
              selectedAmenities={values.amenities}
              disabled={overrides?.amenities ?? true}
            />
            <PmsSyncOption
              value={overrides?.amenities ?? true}
              onChange={(val) => {
                const newOverrides = { ...overrides, amenities: val };
                register("pms_overrides").onChange(newOverrides);
                // When switching ON, mirror the PMS list — including the
                // empty case — so the form doesn't keep a stale manual entry.
                if (val)
                  register("amenities").onChange(
                    unit.pms_values?.amenity_ids ?? []
                  );
              }}
              name="amenities"
            />
          </div>
          <PMSValue
            value={
              unit.pms_values?.amenities?.length
                ? unit.pms_values.amenities.join(", ")
                : undefined
            }
          />
        </div>
      </div>
    </SectionCard>
  );
};

const EditAmenitiesForm = ({
  onClose,
  initialValues,
  unit
}: {
  onClose: () => void;
  initialValues: {
    dogs_allowed?: boolean;
    cats_allowed?: boolean;
    dog_size: DogSizeValue;
    amenities: number[];
  };
  unit: Unit;
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);
  const [petPolicyTouched, setPetPolicyTouched] = useState<
    Record<PetPolicyField, boolean>
  >(emptyPetPolicyTouched);

  const { mutate: updateAmenities, isPending } = useMutation({
    mutationFn: () => {
      const petPolicy = buildPetPolicyPayload(petPolicyTouched, values);
      return instance.patch(`/portal/v1/units/${unit.id}`, {
        amenity_ids: values.amenities,
        ...(Object.keys(petPolicy).length > 0 && { pet_policy: petPolicy })
      });
    },
    onSuccess: () => {
      toast.success("Amenities updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["unit"]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update amenities: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => {
      updateAmenities();
    }
  });

  return (
    <AmenitiesForm
      isUnit
      showDogSize
      level="unit"
      register={register as unknown as Register}
      selectedAmenities={values.amenities}
      onPetPolicyChange={(field) =>
        setPetPolicyTouched((prev) => ({ ...prev, [field]: true }))
      }
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    />
  );
};

interface AmenitiesSectionProps {
  unit: Unit;
}

export const AmenitiesSection = ({ unit }: AmenitiesSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { type } = useAuthContext();
  const { canEditUnitDetails, unitEditDeniedTooltip } =
    useEditDetailsPermissions();
  const isPmsUnit = type.isPmsListingSyncEnabled && !!unit.pms_id;

  if (isEditing) {
    if (isPmsUnit)
      return (
        <PmsEditAmenitiesForm
          onClose={() => setIsEditing(false)}
          initialValues={{
            dogs_allowed: unit.dogs_allowed ?? undefined,
            cats_allowed: unit.cats_allowed ?? undefined,
            dog_size: toDogSizeValue(unit.dog_size),
            // When sync is on, mirror the PMS list (even when empty) so the
            // form doesn't show a stale manual amenity that contradicts
            // PMS Value: N/A.
            amenities: unit.pms_overrides?.amenities
              ? (unit.pms_values?.amenity_ids ?? [])
              : unit.amenities?.map((amenity) => amenity.id) || [],
            pms_overrides: {
              cats_allowed: unit.pms_overrides?.cats_allowed ?? true,
              dogs_allowed: unit.pms_overrides?.dogs_allowed ?? true,
              amenities: unit.pms_overrides?.amenities ?? true
            }
          }}
          unit={unit}
        />
      );

    return (
      <EditAmenitiesForm
        onClose={() => setIsEditing(false)}
        initialValues={{
          dogs_allowed: unit.dogs_allowed ?? undefined,
          cats_allowed: unit.cats_allowed ?? undefined,
          dog_size: toDogSizeValue(unit.dog_size),
          amenities: unit.amenities?.map((amenity) => amenity.id) || []
        }}
        unit={unit}
      />
    );
  }

  return (
    <SectionCard
      title="Amenities"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
          disabled={!canEditUnitDetails}
          tooltip={unitEditDeniedTooltip}
          tooltipPlacement="bottom-end"
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4 w-full">
        <Row title="Cats allowed" value={getYesNo(unit.cats_allowed)} />
        <Row
          title="Dogs allowed"
          value={getDogsAllowedLabel(unit.dogs_allowed, unit.dog_size)}
        />
        <Row
          title="Amenities"
          value={<AmenitiesList amenities={unit.amenities || []} />}
          valueClassName="w-[500px]"
        />
      </div>
    </SectionCard>
  );
};
